import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import referenceService from '../services/referenceService';
import Layout from '../components/Layout';

const CreatePrenupForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    // Basic Information
    NATIONALITY: 'E',
    NATIONALITYIDFK: '',
    NATIONALITYID: '',
    NID: '',
    PASSPORTNUMBER: '',
    FIRSTNAME: '',
    SECONDNAME: '',
    THIRDNAME: '',
    FOURTHNAME: '',
    MARRIAGEADDRESS: '',
    ADDRESS: '',
    PHONE: '',
    GENDER: '',
    BIRTHDATE: '',
    AGE: '',

    // Partner Information
    PARTNERNATIONALITY: 'E',
    PARTNERNID: '',
    PARTNERPASSPORTNUMBER: '',
    PARTNERNAME: '',
    ISRELATEDQUES: 'N',
    PREVMARRIAGEQUES: 'N',
    EXISTCHILDRENQUES: '',
    GENEDISEASEQUES: '',

    // Hidden duplicate fields (legacy - not submitted to backend)
    ISRELATED: 'N',
    ISPREVIOUSMARRIAGE: 'N',
    EXISTINGCHILDREN: '',
    CHILDRENGENETICDISEASES: '',

    // Medical Examinations
    BLDPRESSURE: 'N',
    DIABETIC: 'N',
    RH: 'Y',
    BLOODTRANSFER: '0',
    BLOODPRESSURESYSTOLIC: '',
    BLOODPRESSUREDIASTOLIC: '',
    RANDOMGLUCOSEMEASURE: '',
    HB: '',
    HEIGHT: '',
    WEIGHT: '',
    BLOODTYPEIDFK: '',
    HVDBARCODE: '',
    ANTIHCV: '0',
    HBSAG: '0',
    ANTIHIV: '0'
  });

  const [showNationalityFields, setShowNationalityFields] = useState(false);
  const [showExistingChildrenDiv, setShowExistingChildrenDiv] = useState(false);
  const [showGeneticDiseasesDiv, setShowGeneticDiseasesDiv] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userFoundInDatabase, setUserFoundInDatabase] = useState(false);
  const [fieldsEnabled, setFieldsEnabled] = useState(false);
  const [insuranceMessage, setInsuranceMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  // Reference data states
  const [referenceData, setReferenceData] = useState({
    nationalities: [],
    bloodTypes: [],
    governorates: []
  });
  const [isLoadingReference, setIsLoadingReference] = useState(true);
  
  // Partner search states
  const [isPartnerFieldDisabled, setIsPartnerFieldDisabled] = useState(true);
  const [partnerSearchMessage, setPartnerSearchMessage] = useState('');

  // Load reference data on component mount
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        setIsLoadingReference(true);
        const data = await referenceService.getFormData();
        setReferenceData(data);
      } catch (error) {
        console.error('Failed to load reference data:', error);
      } finally {
        setIsLoadingReference(false);
      }
    };

    loadReferenceData();
  }, []);

  // Function to search for partner by NID
  const searchPartnerByNID = async (nid) => {
    if (!nid || nid.length !== 14) {
      setPartnerSearchMessage('');
      setIsPartnerFieldDisabled(false);
      return;
    }

    // Parse partner NID for birthdate and age
    const partnerParsedData = parseNationalId(nid);

    try {
      const response = await apiService.searchPartnerByNID(nid);
      console.log('Partner search response:', response);

      if (response.success && response.data && response.data.found) {
        // Partner found - auto-fill name and disable field (data integrity)
        console.log('Partner found! Name:', response.data.name);
        setFormData(prev => ({
          ...prev,
          PARTNERNAME: response.data.name,
          PartnerGenderType: partnerParsedData ? partnerParsedData.gender : '',
          PartnerBirthDate: partnerParsedData ? partnerParsedData.birthDate : '',
          PartnerAge: partnerParsedData ? partnerParsedData.age.toString() : ''
        }));
        setIsPartnerFieldDisabled(true);
        setPartnerSearchMessage(`تم العثور على: ${response.data.name}`);
      } else {
        // Partner not found - enable manual entry
        console.log('Partner NOT found - enabling field for manual entry');
        setFormData(prev => ({
          ...prev,
          PartnerGenderType: partnerParsedData ? partnerParsedData.gender : '',
          PartnerBirthDate: partnerParsedData ? partnerParsedData.birthDate : '',
          PartnerAge: partnerParsedData ? partnerParsedData.age.toString() : ''
        }));
        setIsPartnerFieldDisabled(false);
        setPartnerSearchMessage('لم يتم العثور على سجل بهذا الرقم القومي - يمكنك إدخال الاسم يدوياً');
      }
    } catch (error) {
      console.error('Partner search error:', error);
      setIsPartnerFieldDisabled(false);
      setPartnerSearchMessage('خطأ في البحث - يمكنك إدخال الاسم يدوياً');
    }
  };

  // Function to handle Partner National ID search
  const handlePartnerNationalIdSearch = () => {
    // Check if partner NID is same as applicant NID
    if (formData.PARTNERNID === formData.NID) {
      alert('يجب ادخال رقم قومى مختلف');
      setFormData(prev => ({ ...prev, PARTNERNID: '', PARTNERNAME: '' }));
      setIsPartnerFieldDisabled(true);
      return;
    }

    if (!formData.PARTNERNID || formData.PARTNERNID.length !== 14) {
      alert('من فضلك تأكد من رقم البطاقة');
      return;
    }

    // Parse partner NID to extract gender and age
    const partnerParsedData = parseNationalId(formData.PARTNERNID);
    if (!partnerParsedData) {
      alert('من فضلك تأكد من رقم البطاقة');
      return;
    }

    // Check partner age requirement
    if (partnerParsedData.age < 18) {
      alert('لا يمكن إصدار وثيقة, السن القانونى هو ١٨ سنة.');
      setFormData(prev => ({ ...prev, PARTNERNID: '', PARTNERNAME: '', PartnerBirthDate: '', PartnerAge: '' }));
      setIsPartnerFieldDisabled(true);
      return;
    }

    // Validate that partner gender is opposite to applicant gender
    const applicantGender = formData.GENDER;
    const partnerGender = partnerParsedData.gender;

    if (applicantGender === partnerGender) {
      alert('الطرف الاخر يجب ان يكون من الجنس الاخر');
      setFormData(prev => ({ ...prev, PARTNERNID: '', PARTNERNAME: '' }));
      setIsPartnerFieldDisabled(true);
      setPartnerSearchMessage('');
      return;
    }

    // Store partner parsed data
    setFormData(prev => ({
      ...prev,
      PartnerGenderType: partnerGender,
      PartnerBirthDate: partnerParsedData.birthDate,
      PartnerAge: partnerParsedData.age.toString()
    }));

    // If validation passes, proceed with partner search
    searchPartnerByNID(formData.PARTNERNID);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: inputValue
    }));

    // Handle special cases
    if (name === 'NATIONALITY') {
      setShowNationalityFields(value === 'N');
      if (value === 'E') {
        setFormData(prev => ({
          ...prev,
          NATIONALITY: value,
          NATIONALITYIDFK: '',
          NATIONALITYID: '',
          PASSPORTNUMBER: ''
        }));
      }
    }
    
    // Handle nationality dropdown selection for non-Egyptians
    if (name === 'NATIONALITYID') {
      setFormData(prev => ({
        ...prev,
        NATIONALITYID: value,
        NATIONALITYIDFK: value
      }));
    }
    
    if (name === 'PREVMARRIAGEQUES') {
      setShowExistingChildrenDiv(value === 'Y');
      setFormData(prev => ({
        ...prev,
        ISPREVIOUSMARRIAGE: value,
        PREVMARRIAGEQUES: value
      }));
    }
    
    // Handle partner NID changes
    if (name === 'PARTNERNID') {
      setFormData(prev => ({
        ...prev,
        PARTNERNAME: ''
      }));
      setIsPartnerFieldDisabled(false);
      setPartnerSearchMessage('');
    }
    
    // Handle partner nationality changes
    if (name === 'PARTNERNATIONALITY') {
      setFormData(prev => ({
        ...prev,
        PARTNERNID: '',
        PARTNERPASSPORTNUMBER: '',
        PARTNERNAME: ''
      }));
      setIsPartnerFieldDisabled(false);
      setPartnerSearchMessage('');
    }
    
    if (name === 'EXISTCHILDRENQUES') {
      setShowGeneticDiseasesDiv(value === 'Y');
      setFormData(prev => ({
        ...prev,
        EXISTINGCHILDREN: value,
        EXISTCHILDRENQUES: value
      }));
    }
    
    if (name === 'BIRTHDATE' && value) {
      const birthDate = new Date(value);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const calculatedAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
      
      setFormData(prev => ({
        ...prev,
        BIRTHDATE: value,
        AGE: calculatedAge.toString()
      }));
    }
  };

  // Function to parse Egyptian National ID
  const parseNationalId = (nationalId) => {
    if (!nationalId || nationalId.length !== 14) {
      return null;
    }

    try {
      // Extract birth date from National ID
      const century = nationalId.charAt(0);
      const year = nationalId.substring(1, 3);
      const month = nationalId.substring(3, 5);
      const day = nationalId.substring(5, 7);
      
      // Determine full year based on century digit
      let fullYear;
      if (century === '2') {
        fullYear = parseInt('19' + year);
      } else if (century === '3') {
        fullYear = parseInt('20' + year);
      } else {
        return null;
      }
      
      // Create birth date
      const birthDate = new Date(fullYear, parseInt(month) - 1, parseInt(day));
      
      // Check if date is valid
      if (birthDate.getFullYear() !== fullYear || 
          birthDate.getMonth() !== parseInt(month) - 1 || 
          birthDate.getDate() !== parseInt(day)) {
        return null;
      }
      
      // Extract gender from 13th digit (odd = male, even = female)
      const genderDigit = parseInt(nationalId.charAt(12));
      const gender = genderDigit % 2 === 1 ? 'M' : 'F';
      
      // Calculate age
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      // Format birth date for input (YYYY-MM-DD)
      const formattedBirthDate = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      
      return {
        birthDate: formattedBirthDate,
        gender: gender,
        age: age
      };
    } catch (error) {
      return null;
    }
  };

  // Function to handle passport search for non-Egyptian
  const handlePassportSearch = () => {
    if (formData.NATIONALITY === 'N') {
      if (!formData.PASSPORTNUMBER || formData.PASSPORTNUMBER.trim() === '') {
        alert('من فضلك تأكد من رقم جواز السفر');
        return;
      }
      // Passport should be at least 6 characters
      if (formData.PASSPORTNUMBER.trim().length < 6) {
        alert('رقم جواز السفر يجب أن يكون 6 أحرف على الأقل');
        return;
      }
      // Enable fields for manual entry for non-Egyptian
      setUserFoundInDatabase(false);
      setFieldsEnabled(true);
      setInsuranceMessage('');
    }
  };

  // Function to handle National ID search
  const handleNationalIdSearch = async () => {
    if (!formData.NID || formData.NID.length !== 14) {
      alert('من فضلك تأكد من رقم البطاقة');
      return;
    }

    // Parse NID for birthdate, gender, age
    const parsedData = parseNationalId(formData.NID);
    if (!parsedData) {
      alert('من فضلك تأكد من رقم البطاقة');
      return;
    }

    // Check age requirement
    if (parsedData.age < 18) {
      alert('لا يمكن إصدار وثيقة, السن القانونى هو ١٨ سنة.');
      setFormData(prev => ({ ...prev, NID: '', BIRTHDATE: '', AGE: '', GENDER: '' }));
      return;
    }

    // Set parsed data
    setFormData(prev => ({
      ...prev,
      BIRTHDATE: parsedData.birthDate,
      GENDER: parsedData.gender,
      AGE: parsedData.age.toString()
    }));

    // Search for user in database
    try {
      const response = await apiService.searchUserByNID(formData.NID);
      console.log('User search response:', response);

      if (response.success && response.data && response.data.found) {
        // User found in database - populate all fields and disable them
        console.log('User found! Name:', response.data.name);
        setUserFoundInDatabase(true);
        setFieldsEnabled(false);
        setInsuranceMessage(''); // User has insurance

        setFormData(prev => ({
          ...prev,
          FIRSTNAME: response.data.name.split(' ')[0] || '',
          SECONDNAME: response.data.name.split(' ')[1] || '',
          THIRDNAME: response.data.name.split(' ')[2] || '',
          FOURTHNAME: response.data.name.split(' ')[3] || '',
          BIRTHDATE: parsedData.birthDate,
          GENDER: parsedData.gender,
          AGE: parsedData.age.toString()
        }));
      } else {
        // User not found - enable fields for manual entry
        setUserFoundInDatabase(false);
        setFieldsEnabled(true);
        setInsuranceMessage('هذا المواطن غير مغطى تأمينياً');
      }
    } catch (error) {
      console.error('Error searching user:', error);
      // On error, enable fields for manual entry
      setUserFoundInDatabase(false);
      setFieldsEnabled(true);
      setInsuranceMessage('هذا المواطن غير مغطى تأمينياً');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all required fields are filled
    if (!formData.NID && !formData.PASSPORTNUMBER) {
      alert('برجاء التأكد من ملئ كل البيانات.');
      return;
    }

    if (!formData.FIRSTNAME || !formData.SECONDNAME || !formData.THIRDNAME) {
      alert('برجاء التأكد من ملئ كل البيانات.');
      return;
    }

    if (!formData.PARTNERNID && !formData.PARTNERPASSPORTNUMBER) {
      alert('برجاء التأكد من ملئ كل البيانات.');
      return;
    }

    if (!formData.PARTNERNAME) {
      alert('برجاء التأكد من ملئ كل البيانات.');
      return;
    }

    // Validate barcode
    if (formData.HVDBARCODE) {
      if (formData.HVDBARCODE.length !== 12) {
        alert('يجب أن يحتوى الباركود على 12 رقم');
        return;
      }
      if (!formData.HVDBARCODE.startsWith('20')) {
        alert('يجب أن يبدأ الباركود برقم 20');
        return;
      }
    }

    // Validate phone number for Egyptian nationality
    if (formData.NATIONALITY === 'E' && formData.PHONE) {
      if (formData.PHONE.length !== 11) {
        alert('ادخل رقم التليفون صحيح بحيث يتكون من 11 رقم');
        return;
      }
      if (!/^(01)([0-9]{9})$/.test(formData.PHONE)) {
        alert('ادخل رقم التليفون صحيح');
        return;
      }
    }

    // Validate blood pressure values
    const systolic = parseInt(formData.BLOODPRESSURESYSTOLIC);
    const diastolic = parseInt(formData.BLOODPRESSUREDIASTOLIC);
    if (systolic <= diastolic) {
      alert('خطأ في قيم ضغط الدم: الضغط الانقباضي يجب أن يكون أكبر من الضغط الانبساطي');
      return;
    }

    setIsSubmitting(true);

    try {
      // Map the form data to the database schema format
      const dataToSubmit = {
        // Basic Information
        NATIONALITY: formData.NATIONALITY,
        ...(formData.NATIONALITY === 'N' && formData.NATIONALITYIDFK && { NATIONALITYIDFK: parseInt(formData.NATIONALITYIDFK) }),
        NATIONALITYID: formData.NATIONALITY === 'N' ? (formData.NATIONALITYID || '') : '',
        NID: formData.NATIONALITY === 'E' ? formData.NID : '',
        PASSPORTNUMBER: formData.NATIONALITY === 'N' ? formData.PASSPORTNUMBER : '',
        FIRSTNAME: formData.FIRSTNAME,
        SECONDNAME: formData.SECONDNAME,
        THIRDNAME: formData.THIRDNAME,
        FOURTHNAME: formData.FOURTHNAME || '',
        MARRIAGEADDRESS: formData.MARRIAGEADDRESS || '',
        ADDRESS: formData.ADDRESS,
        PHONE: formData.PHONE,
        GENDER: formData.GENDER,
        BIRTHDATE: formData.BIRTHDATE,
        AGE: parseInt(formData.AGE) || 0,
        
        // Partner Information
        PARTNERNATIONALITY: formData.PARTNERNATIONALITY,
        PARTNERNID: formData.PARTNERNATIONALITY === 'E' ? formData.PARTNERNID : '',
        PARTNERPASSPORTNUMBER: formData.PARTNERNATIONALITY === 'N' ? formData.PARTNERPASSPORTNUMBER : '',
        PARTNERNAME: formData.PARTNERNAME,
        
        // Relationship Questions
        ISRELATEDQUES: formData.ISRELATEDQUES,
        PREVMARRIAGEQUES: formData.PREVMARRIAGEQUES,
        EXISTCHILDRENQUES: formData.EXISTCHILDRENQUES,
        GENEDISEASEQUES: formData.GENEDISEASEQUES,

        // Medical Information
        BLDPRESSURE: formData.BLDPRESSURE,
        DIABETIC: formData.DIABETIC,
        RH: formData.RH,
        BLOODTRANSFER: formData.BLOODTRANSFER,

        // Medical Measurements
        BLOODPRESSURESYSTOLIC: parseInt(formData.BLOODPRESSURESYSTOLIC) || 0,
        BLOODPRESSUREDIASTOLIC: parseInt(formData.BLOODPRESSUREDIASTOLIC) || 0,
        RANDOMGLUCOSEMEASURE: parseInt(formData.RANDOMGLUCOSEMEASURE) || 0,
        HB: parseFloat(formData.HB) || 0,
        HEIGHT: parseInt(formData.HEIGHT) || 0,
        WEIGHT: parseInt(formData.WEIGHT) || 0,
        BLOODTYPEIDFK: formData.BLOODTYPEIDFK,
        HVDBARCODE: formData.HVDBARCODE,

        // Virus Tests - convert 0/1 to N/Y for backend
        ANTIHCV: formData.ANTIHCV === '1' ? 'Y' : 'N',
        HBSAG: formData.HBSAG === '1' ? 'Y' : 'N',
        ANTIHIV: formData.ANTIHIV === '1' ? 'Y' : 'N'
      };

      // DEBUG: Log conversion details
      console.log('=== FORM SUBMISSION DEBUG ===');
      console.log('Raw formData ISRELATEDQUES:', formData.ISRELATEDQUES);
      console.log('Raw formData ANTIHCV:', formData.ANTIHCV);
      console.log('Raw formData HBSAG:', formData.HBSAG);
      console.log('Raw formData ANTIHIV:', formData.ANTIHIV);
      console.log('---');
      console.log('Converted ISRELATEDQUES:', dataToSubmit.ISRELATEDQUES);
      console.log('Converted ANTIHCV:', dataToSubmit.ANTIHCV);
      console.log('Converted HBSAG:', dataToSubmit.HBSAG);
      console.log('Converted ANTIHIV:', dataToSubmit.ANTIHIV);
      console.log('---');
      console.log('Full dataToSubmit:', JSON.stringify(dataToSubmit, null, 2));
      console.log('=========================');

      const response = await apiService.createPrenup(dataToSubmit);
      if (response.success) {
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error creating prenup:', error);
      // Check for specific error messages
      if (error.response && error.response.data) {
        const { message: errorMsg, details, errors: validationErrors } = error.response.data;

        // Check for duplicate key errors using the details field from backend
        if (details && details.isDuplicate) {
          if (details.field === 'HVDBARCODE') {
            alert('هذا الباركود مستخدم من قبل. برجاء إعادة مسح الباركود أو إدخال باركود جديد');
          } else if (details.field === 'NID') {
            alert('هذا الرقم القومي مسجل من قبل');
          } else {
            alert(errorMsg || 'البيانات مكررة');
          }
        } else if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
          // Show validation errors (from express-validator)
          alert(validationErrors.join('\n'));
        } else if (errorMsg) {
          // Show the backend error message directly (already in Arabic)
          alert(errorMsg);
        } else {
          alert('لم يتم حفظ الاستمارة');
        }
      } else {
        alert('لم يتم حفظ الاستمارة');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate('/prenup/PreMarriageSheet/MarriageIndex?IsPrint=False');
  };

  return (
    <Layout>
      <main role="main">
        <div className="container mt-5">
          <div className="row">
            <h3>استمارة فحص المقبلين على الزواج</h3>
            <br />

        <form
          onSubmit={handleSubmit}
          className="needs-validation"
          encType="multipart/form-data"
          id="createForm"
          name="createForm"
        >
          <div className="col-md-12 patient-info" id="infoDiv">
            
            <h2>البيانات الأساسية</h2>
            <hr />

            <div className="row">
              <div className="col-md-3 form-group">
                <label>
                  <i className="fas fa-flag"></i> الجنسية <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="form-check">
                    <input 
                      className="form-check-input ignore" 
                      type="radio" 
                      name="NATIONALITY" 
                      id="RadioNATIONALITY_E" 
                      value="E" 
                      onChange={handleInputChange}
                      checked={formData.NATIONALITY === 'E'}
                      required 
                      title="يجب اختيار جنسية"
                    />
                    <label className="form-check-label" htmlFor="RadioNATIONALITY_E">
                      مصري
                    </label>
                  </div>
                  <div className="form-check">
                    <input 
                      className="form-check-input ignore" 
                      type="radio" 
                      name="NATIONALITY" 
                      id="RadioNATIONALITY_N" 
                      value="N" 
                      onChange={handleInputChange}
                      checked={formData.NATIONALITY === 'N'}
                      required 
                      title="يجب اختيار جنسية"
                    />
                    <label className="form-check-label" htmlFor="RadioNATIONALITY_N">
                      غير مصري
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3 form-group" id="NATIONALITYDIV" hidden={!showNationalityFields}>
                <label>
                  <i className="fas fa-location-arrow"></i> بلد الجنسية <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <label style={{width: '21em'}}>
                    <input
                      className="ignore"
                      id="NATIONALITYIDFK"
                      name="NATIONALITYIDFK"
                      type="hidden"
                      value={formData.NATIONALITYIDFK}
                      onChange={handleInputChange}
                    />
                    <select
                      className="custom-select d-block w-100 col-md-12 ignore"
                      id="NATIONALITYID"
                      name="NATIONALITYID"
                      value={formData.NATIONALITYID}
                      onChange={handleInputChange}
                      required={formData.NATIONALITY === 'N'}
                      title="برجاء الاختيار"
                      disabled={isLoadingReference}
                    >
                      <option value="">{isLoadingReference ? 'جاري التحميل...' : 'اختر'}</option>
                      {referenceData.nationalities.map(nationality => (
                        <option key={nationality.id} value={nationality.id}>
                          {nationality.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
              
              <div className="col-md-6 form-group">
                <i className="fas fa-address-card"></i> <label id="NIDLABEL">
                  {formData.NATIONALITY === 'E' ? 'الرقم القومي' : 'رقم جواز السفر'}
                </label><span className="text-danger">*</span>
                <div className="input-group">
                  {/* Egyptian - NID field */}
                  {formData.NATIONALITY === 'E' && (
                    <>
                      <input
                        className="form-control"
                        style={{width: '21em'}}
                        disabled={userFoundInDatabase}
                        id="NID"
                        name="NID"
                        value={formData.NID}
                        onChange={handleInputChange}
                        placeholder="الرقم القومي"
                        minLength="14"
                        maxLength="14"
                        pattern="[0-9]{14}"
                        required
                        title="ادخل الرقم القومى صحيح بحيث يتكون من 14 رقم"
                        type="text"
                        inputMode="numeric"
                      />
                      <span className="input-group-btn">
                        <button
                          className="btn btn-custom-info"
                          type="button"
                          id="searchBtn"
                          disabled={userFoundInDatabase}
                          onClick={handleNationalIdSearch}
                        >
                          <i className="fa fa-search"></i>
                        </button>
                      </span>
                    </>
                  )}
                  {/* Non-Egyptian - Passport field */}
                  {formData.NATIONALITY === 'N' && (
                    <>
                      <input
                        className="form-control"
                        style={{width: '21em'}}
                        disabled={userFoundInDatabase}
                        id="PASSPORTNUMBER"
                        name="PASSPORTNUMBER"
                        value={formData.PASSPORTNUMBER}
                        onChange={handleInputChange}
                        placeholder="رقم جواز السفر"
                        required
                        title="ادخل رقم جواز السفر"
                        type="text"
                      />
                      <span className="input-group-btn">
                        <button
                          className="btn btn-custom-info"
                          type="button"
                          id="searchBtn"
                          disabled={userFoundInDatabase}
                          onClick={handlePassportSearch}
                        >
                          <i className="fa fa-search"></i>
                        </button>
                      </span>
                    </>
                  )}
                </div>
                <div id="hioText" className="text-danger" style={{fontWeight: 'bold'}}>
                  {insuranceMessage}
                </div>
                <input type="hidden" id="hasHIO" value={insuranceMessage ? '' : '1'} />
              </div>
            </div>
            
            <label>الاسم (كما هو مدون بالبطاقة أو وثيقة السفر)</label>

            <div className="row">
              <div className="col-md-3 form-group">
                <label><i className="fas fa-user"></i> الاسم الأول <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  disabled={!fieldsEnabled}
                  id="FIRSTNAME"
                  name="FIRSTNAME"
                  value={formData.FIRSTNAME}
                  onChange={handleInputChange}
                  required
                  title="ادخل الاسم الأول"
                  type="text"
                />
                <span className="field-validation-valid text-danger" data-valmsg-for="PATIENT.FIRSTNAME" data-valmsg-replace="true"></span>
              </div>
              <div className="col-md-3 form-group">
                <label><i className="fas fa-user"></i> الاسم الثانى <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  disabled={!fieldsEnabled}
                  id="SECONDNAME"
                  name="SECONDNAME"
                  value={formData.SECONDNAME}
                  onChange={handleInputChange}
                  required
                  title="ادخل الاسم الثانى"
                  type="text"
                />
              </div>
              <div className="col-md-3 form-group">
                <label><i className="fas fa-user"></i> الاسم الثالث <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  disabled={!fieldsEnabled}
                  id="THIRDNAME"
                  name="THIRDNAME"
                  value={formData.THIRDNAME}
                  onChange={handleInputChange}
                  required
                  title="ادخل الاسم الثالث"
                  type="text"
                />
              </div>
              <div className="col-md-3 form-group">
                <label><i className="fas fa-user"></i> الاسم الرابع </label>
                <input
                  className="form-control"
                  disabled={!fieldsEnabled}
                  id="FOURTHNAME"
                  name="FOURTHNAME"
                  value={formData.FOURTHNAME}
                  onChange={handleInputChange}
                  type="text"
                />
              </div>
            </div>
            
            <div className="row">
              <div className="col-md-4 form-group">
                <label><i className="fas fa-home"></i> عنوان سكن الزوجية </label>
                <input
                  className="form-control"
                  disabled={!fieldsEnabled}
                  id="MARRIAGEADDRESS"
                  name="MARRIAGEADDRESS"
                  value={formData.MARRIAGEADDRESS}
                  onChange={handleInputChange}
                  type="text"
                />
              </div>
              <div className="col-md-4 form-group">
                <label><i className="fas fa-home"></i> العنوان بالبطاقة <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  disabled={!fieldsEnabled}
                  id="ADDRESS"
                  name="ADDRESS"
                  value={formData.ADDRESS}
                  onChange={handleInputChange}
                  required
                  title="ادخل العنوان بالبطاقة "
                  type="text"
                />
              </div>
              <div className="col-md-4 form-group">
                <label>
                  <i className="fas fa-mobile"></i>
                  رقم الهاتف <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  disabled={!fieldsEnabled}
                  id="PHONE"
                  name="PHONE"
                  value={formData.PHONE}
                  onChange={handleInputChange}
                  placeholder="0100 000 0000"
                  minLength={formData.NATIONALITY === 'E' ? "11" : undefined}
                  maxLength={formData.NATIONALITY === 'E' ? "11" : undefined}
                  pattern={formData.NATIONALITY === 'E' ? "^(01)([0-9]{9})$" : undefined}
                  required
                  title="ادخل رقم التليفون صحيح بحيث يتكون من 11 رقم"
                  type="tel"
                />
              </div>
            </div>
            
            <div className="row">
              <div className="col-md-4 form-group">
                <label><i className="fas fa-venus-mars"></i> النوع <span className="text-danger">*</span></label>
                <select
                  className="custom-select d-block w-100"
                  id="GENDER"
                  name="GENDER"
                  value={formData.GENDER}
                  onChange={handleInputChange}
                  required
                  title="اختر النوع"
                  disabled={!(formData.NATIONALITY === 'N' && fieldsEnabled)}
                >
                  <option value="">اختر...</option>
                  <option value="M">ذكر</option>
                  <option value="F">أنثى</option>
                </select>
              </div>
              <div className="col-md-4 form-group">
                <label><i className="fas fa-calendar-alt"></i> تاريخ الميلاد <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  id="BIRTHDATE"
                  name="BIRTHDATE"
                  value={formData.BIRTHDATE}
                  onChange={handleInputChange}
                  readOnly={!(formData.NATIONALITY === 'N' && fieldsEnabled)}
                  required
                  title="ادخل تاريخ الميلاد"
                  type="date"
                />
              </div>
              <div className="col-md-4 form-group">
                <label><i className="fas fa-calendar"></i> السن <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  disabled="disabled"
                  id="AGE"
                  name="AGE"
                  value={formData.AGE}
                  type="number"
                />
              </div>
            </div>
          </div>
          
          <div className="col-md-12 patient-info">
            <h2>بيانات الزوج/الزوجة</h2>
            <hr />

            <div className="row" style={{fontSize: '16px'}}>
              <div className="col-md-3 form-group">
                <label>
                  <i className="fas fa-flag"></i> الجنسية <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="form-check">
                    <input 
                      className="form-check-input ignore" 
                      type="radio" 
                      name="PARTNERNATIONALITY" 
                      id="RadioPartnerNATIONALITY_E" 
                      value="E" 
                      onChange={handleInputChange}
                      checked={formData.PARTNERNATIONALITY === 'E'}
                      required 
                      title="يجب اختيار جنسية"
                    />
                    <label className="form-check-label" htmlFor="RadioPartnerNATIONALITY_E">
                      مصري
                    </label>
                  </div>
                  <div className="form-check">
                    <input 
                      className="form-check-input ignore" 
                      type="radio" 
                      name="PARTNERNATIONALITY" 
                      id="RadioPartnerNATIONALITY_N" 
                      value="N" 
                      onChange={handleInputChange}
                      checked={formData.PARTNERNATIONALITY === 'N'}
                      required 
                      title="يجب اختيار جنسية"
                    />
                    <label className="form-check-label" htmlFor="RadioPartnerNATIONALITY_N">
                      غير مصري
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="col-md-4 form-group">
                <i className="fas fa-address-card"></i> <label id="PARTNERNIDLABEL">
                  {formData.PARTNERNATIONALITY === 'E' ? 'رقم قومى الطرف الاخر' : 'رقم جواز سفر الطرف الاخر'}
                </label><span className="text-danger">*</span>
                <div className="input-group">
                  {/* Egyptian partner - NID field */}
                  {formData.PARTNERNATIONALITY === 'E' && (
                    <>
                      <input
                        className="form-control"
                        id="PARTNERNID"
                        name="PARTNERNID"
                        value={formData.PARTNERNID}
                        onChange={handleInputChange}
                        placeholder="رقم قومى الطرف الاخر"
                        minLength="14"
                        maxLength="14"
                        pattern="[0-9]{14}"
                        required
                        title="من فضلك تأكد من رقم البطاقة"
                        type="text"
                        inputMode="numeric"
                      />
                      <span className="input-group-btn">
                        <button
                          className="btn btn-custom-info"
                          type="button"
                          id="searchBtnn"
                          onClick={handlePartnerNationalIdSearch}
                        >
                          <i className="fa fa-search"></i>
                        </button>
                      </span>
                    </>
                  )}
                  {/* Non-Egyptian partner - Passport field */}
                  {formData.PARTNERNATIONALITY === 'N' && (
                    <input
                      className="form-control"
                      id="PARTNERPASSPORTNUMBER"
                      name="PARTNERPASSPORTNUMBER"
                      value={formData.PARTNERPASSPORTNUMBER}
                      onChange={handleInputChange}
                      placeholder="رقم جواز سفر الطرف الاخر"
                      required
                      title="من فضلك أدخل رقم جواز السفر"
                      type="text"
                    />
                  )}
                </div>
                {partnerSearchMessage && formData.PARTNERNATIONALITY === 'E' && (
                  <div className="text-info" style={{fontSize: '12px', marginTop: '5px'}}>
                    {partnerSearchMessage}
                  </div>
                )}
              </div>

              <div className="col-md-4 form-group">
                <label>
                  اسم الطرف الاخر <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  disabled={formData.PARTNERNATIONALITY === 'E' && isPartnerFieldDisabled}
                  id="PARTNERNAME"
                  name="PARTNERNAME"
                  value={formData.PARTNERNAME}
                  onChange={handleInputChange}
                  placeholder=" اسم الطرف الاخر"
                  required
                  title="برجاء ادخال اسم الطرف الاخر "
                  type="text"
                />
              </div>
            </div>

            <div className="row" style={{fontSize: '16px'}}>
              <div className="col-md-4 form-group">
                <input id="ISRELATED" name="ISRELATED" type="hidden" value={formData.ISRELATED} />

                <label>
                  هل توجد قرابة مع الطرف الاخر <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="ISRELATEDQUES" 
                      id="RadioRelatedY" 
                      value="Y" 
                      onChange={(e) => {
                        handleInputChange(e);
                        setFormData(prev => ({...prev, ISRELATED: 'Y'}));
                      }}
                      checked={formData.ISRELATEDQUES === 'Y'}
                      required 
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioRelatedY">
                      نعم
                    </label>
                  </div>
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="ISRELATEDQUES" 
                      id="RadioRelatedN" 
                      value="N" 
                      onChange={(e) => {
                        handleInputChange(e);
                        setFormData(prev => ({...prev, ISRELATED: 'N'}));
                      }}
                      checked={formData.ISRELATEDQUES === 'N'}
                      required 
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioRelatedN">
                      لا
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="col-md-4 form-group">
                <input id="ISPREVIOUSMARRIAGE" name="ISPREVIOUSMARRIAGE" type="hidden" value={formData.ISPREVIOUSMARRIAGE} />

                <label>
                  هل يوجد زواج سابق <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="PREVMARRIAGEQUES" 
                      id="RadioPrevMarriageY" 
                      value="Y" 
                      onChange={handleInputChange}
                      checked={formData.PREVMARRIAGEQUES === 'Y'}
                      required 
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioPrevMarriageY">
                      نعم
                    </label>
                  </div>
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="PREVMARRIAGEQUES" 
                      id="RadioPrevMarriageN" 
                      value="N" 
                      onChange={handleInputChange}
                      checked={formData.PREVMARRIAGEQUES === 'N'}
                      required 
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioPrevMarriageN">
                      لا
                    </label>
                  </div>
                </div>
              </div>
              
            </div>

            <div className="row" style={{fontSize: '16px'}}>
              {showExistingChildrenDiv && (
                <div className="col-md-4 form-group" id="EXISTCHILDRENDIV">
                  <input id="EXISTINGCHILDREN" name="EXISTINGCHILDREN" type="hidden" value={formData.EXISTINGCHILDREN} />

                  <label>
                    هل يوجد اطفال من الزواج السابق <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="radio" 
                        name="EXISTCHILDRENQUES" 
                        id="RadioExistChildY" 
                        value="Y" 
                        onChange={handleInputChange}
                        checked={formData.EXISTCHILDRENQUES === 'Y'}
                        required 
                        title="برجاء الاختيار"
                      />
                      <label className="form-check-label" htmlFor="RadioExistChildY">
                        نعم
                      </label>
                    </div>
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="radio" 
                        name="EXISTCHILDRENQUES" 
                        id="RadioExistChildN" 
                        value="N" 
                        onChange={handleInputChange}
                        checked={formData.EXISTCHILDRENQUES === 'N'}
                        required 
                        title="برجاء الاختيار"
                      />
                      <label className="form-check-label" htmlFor="RadioExistChildN">
                        لا
                      </label>
                    </div>
                  </div>
                </div>
              )}
              
              {showGeneticDiseasesDiv && (
                <div className="col-md-4 form-group" id="GENEDISEASESDIV">
                  <input id="CHILDRENGENETICDISEASES" name="CHILDRENGENETICDISEASES" type="hidden" value={formData.CHILDRENGENETICDISEASES} />

                  <label>
                    هل يوجد لدى الاطفال اى امراض وراثية <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="radio" 
                        name="GENEDISEASEQUES" 
                        id="RadioGeneDiseaseY" 
                        value="Y" 
                        onChange={(e) => {
                          handleInputChange(e);
                          setFormData(prev => ({...prev, CHILDRENGENETICDISEASES: 'Y'}));
                        }}
                        checked={formData.GENEDISEASEQUES === 'Y'}
                        required 
                        title="برجاء الاختيار"
                      />
                      <label className="form-check-label" htmlFor="RadioGeneDiseaseY">
                        نعم
                      </label>
                    </div>
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="radio" 
                        name="GENEDISEASEQUES" 
                        id="RadioGeneDiseaseN" 
                        value="N" 
                        onChange={(e) => {
                          handleInputChange(e);
                          setFormData(prev => ({...prev, CHILDRENGENETICDISEASES: 'N'}));
                        }}
                        checked={formData.GENEDISEASEQUES === 'N'}
                        required 
                        title="برجاء الاختيار"
                      />
                      <label className="form-check-label" htmlFor="RadioGeneDiseaseN">
                        لا
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="col-md-12 patient-info">
            <h2>الفحوصات الطبية</h2>
            <hr />

            <div className="row" style={{fontSize: '16px'}}>
              <div className="col-md-3 form-group">
                <input id="ISBLOODPRESSURE" name="ISBLOODPRESSURE" type="hidden" value={formData.BLDPRESSURE} />

                <label>
                  مريض ضغط الدم <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="BLDPRESSURE" 
                      id="RadioBldPressureY" 
                      value="Y" 
                      onChange={(e) => {
                        handleInputChange(e);
                        setFormData(prev => ({...prev, BLDPRESSURE: 'Y'}));
                      }}
                      checked={formData.BLDPRESSURE === 'Y'}
                      required 
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioBldPressureY">
                      نعم
                    </label>
                  </div>
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="BLDPRESSURE" 
                      id="RadioBldPressureN" 
                      value="N" 
                      onChange={(e) => {
                        handleInputChange(e);
                        setFormData(prev => ({...prev, BLDPRESSURE: 'N'}));
                      }}
                      checked={formData.BLDPRESSURE === 'N'}
                      required 
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioBldPressureN">
                      لا
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3 form-group">
                <input id="ISDIABETIC" name="ISDIABETIC" type="hidden" value={formData.DIABETIC} />

                <label>
                  مريض سكر <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="DIABETIC" 
                      id="RadioDiabeticY" 
                      value="Y" 
                      onChange={(e) => {
                        handleInputChange(e);
                        setFormData(prev => ({...prev, DIABETIC: 'Y'}));
                      }}
                      checked={formData.DIABETIC === 'Y'}
                      required 
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioDiabeticY">
                      نعم
                    </label>
                  </div>
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="DIABETIC" 
                      id="RadioDiabeticN" 
                      value="N" 
                      onChange={(e) => {
                        handleInputChange(e);
                        setFormData(prev => ({...prev, DIABETIC: 'N'}));
                      }}
                      checked={formData.DIABETIC === 'N'}
                      required 
                      title="برجaaاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioDiabeticN">
                      لا
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3 form-group">
                <input id="ISRH" name="ISRH" type="hidden" value={formData.RH} />

                <label>
                  Rh <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="RH"
                      id="RadioRHY"
                      value="Y"
                      onChange={(e) => {
                        handleInputChange(e);
                        setFormData(prev => ({...prev, ISRH: 'Y'}));
                      }}
                      checked={formData.RH === 'Y'}
                      required
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioRHY">
                      إيجابى
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="RH"
                      id="RadioRHN"
                      value="N"
                      onChange={(e) => {
                        handleInputChange(e);
                        setFormData(prev => ({...prev, ISRH: 'N'}));
                      }}
                      checked={formData.RH === 'N'}
                      required
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioRHN">
                      سلبى
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="row" style={{fontSize: '16px'}}>
              <div className="col-md-3 form-group">
                <label> قياس ضغط الدم <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  id="BLOODPRESSURESYSTOLIC"
                  name="BLOODPRESSURESYSTOLIC"
                  value={formData.BLOODPRESSURESYSTOLIC}
                  onChange={handleInputChange}
                  max="260"
                  min="60"
                  placeholder=" SYSTOLIC"
                  required
                  title="برجاء ادخال رقم بين 60 و 260"
                  type="text"
                  inputMode="numeric"
                />
                <span className="field-validation-valid text-danger" data-valmsg-for="BLOODPRESSURESYSTOLIC" data-valmsg-replace="true"></span>
                <br />
                <input
                  className="form-control"
                  id="BLOODPRESSUREDIASTOLIC"
                  name="BLOODPRESSUREDIASTOLIC"
                  value={formData.BLOODPRESSUREDIASTOLIC}
                  onChange={handleInputChange}
                  max="150"
                  min="30"
                  placeholder=" DIASTOLIC"
                  required
                  title="برجاء ادخال رقم بين 30 و 150"
                  type="text"
                  inputMode="numeric"
                />
                <span className="field-validation-valid text-danger" data-valmsg-for="BLOODPRESSUREDIASTOLIC" data-valmsg-replace="true"></span>
              </div>
              <div className="col-md-3 form-group">
                <label>
                  قياس السكر العشوائى <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  id="RANDOMGLUCOSEMEASURE"
                  name="RANDOMGLUCOSEMEASURE"
                  value={formData.RANDOMGLUCOSEMEASURE}
                  onChange={handleInputChange}
                  max="600"
                  min="70"
                  required
                  title="برجاء ادخال رقم بين 70 و 600"
                  type="text"
                  inputMode="decimal"
                />
                <span className="field-validation-valid text-danger" data-valmsg-for="RANDOMGLUCOSEMEASURE" data-valmsg-replace="true"></span>
              </div>

              <div className="col-md-3 form-group">
                <label>
                  Hb <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  id="HB"
                  name="HB"
                  value={formData.HB}
                  onChange={handleInputChange}
                  max="25"
                  min="1"
                  required
                  step="any"
                  title="برجاء ادخال رقم بين 1 و 25"
                  type="text"
                  inputMode="decimal"
                />
                <span className="field-validation-valid text-danger" data-valmsg-for="HB" data-valmsg-replace="true"></span>
              </div>
            </div>

            <div className="row" style={{fontSize: '16px'}}>
              <div className="col-md-3 form-group">
                <label> الطول (سم) <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  id="HEIGHT"
                  name="HEIGHT"
                  value={formData.HEIGHT}
                  onChange={handleInputChange}
                  max="300"
                  min="50"
                  required
                  title="برجاء ادخال رقم بين 50 و 300"
                  type="text"
                  inputMode="decimal"
                />
                <span className="field-validation-valid text-danger" data-valmsg-for="HEIGHT" data-valmsg-replace="true"></span>
                <br />
              </div>
              <div className="col-md-3 form-group">
                <label>
                  الوزن (كجم) <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  id="WEIGHT"
                  name="WEIGHT"
                  value={formData.WEIGHT}
                  onChange={handleInputChange}
                  max="250"
                  min="40"
                  required
                  title="برجاء ادخال رقم بين 40 و 250"
                  type="text"
                  inputMode="decimal"
                />
                <span className="field-validation-valid text-danger" data-valmsg-for="WEIGHT" data-valmsg-replace="true"></span>
              </div>
            </div>

            <div className="row" style={{fontSize: '16px'}}>
              <div className="col-md-6 form-group">
                <input id="ISBLOODTRANSFER" name="ISBLOODTRANSFER" type="hidden" value={formData.BLOODTRANSFER} />

                <label>
                  هل تم عمل نقل دم فى أخر ستة أشهر؟ <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="BLOODTRANSFER"
                      id="RadioBloodTransferY"
                      value="1"
                      onChange={handleInputChange}
                      checked={formData.BLOODTRANSFER === '1'}
                      required
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioBloodTransferY">
                      نعم
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="BLOODTRANSFER"
                      id="RadioBloodTransferN"
                      value="0"
                      onChange={handleInputChange}
                      checked={formData.BLOODTRANSFER === '0'}
                      required
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioBloodTransferN">
                      لا
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-md-3 form-group">
                <label>
                  Filter Paper Barcode <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  id="HVDBARCODE"
                  name="HVDBARCODE"
                  value={formData.HVDBARCODE}
                  onChange={handleInputChange}
                  minLength="12"
                  maxLength="12"
                  pattern="^20[0-9]{10}$"
                  required
                  title="يجب أن يحتوى الباركود على 12 رقم ويبدأ برقم 20"
                  type="text"
                />
                <span className="field-validation-valid text-danger" data-valmsg-for="HVDBARCODE" data-valmsg-replace="true"></span>
                <div id="HVDerror" style={{color: 'red'}} tabIndex='1' hidden>يجب أن يبدأ الباركود برقم 20</div>
              </div>
            </div>
            <hr />
            
            <div className="row" style={{fontSize: '16px'}}>
              <div id="ISANTIHCVDIV" className="col-md-3 form-group">
                <input id="ISANTIHCV" name="ISANTIHCV" type="hidden" value={formData.ANTIHCV} />

                <label>
                  Anti-HCV <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ANTIHCV"
                      id="RadioISANTIHCVY"
                      value="1"
                      onChange={handleInputChange}
                      checked={formData.ANTIHCV === '1'}
                      required
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioISANTIHCVY">
                      متفاعل
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ANTIHCV"
                      id="RadioISANTIHCVN"
                      value="0"
                      onChange={handleInputChange}
                      checked={formData.ANTIHCV === '0'}
                      required
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioISANTIHCVN">
                      غير متفاعل
                    </label>
                  </div>
                </div>
                <span className="field-validation-valid text-danger" data-valmsg-for="ISANTIHCV" data-valmsg-replace="true"></span>
              </div>
              <div id="ISHBSAGDIV" className="col-md-3 form-group">
                <input id="ISHBSAG" name="ISHBSAG" type="hidden" value={formData.HBSAG} />

                <label>
                  HBsAg <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="HBSAG"
                      id="RadioISHBSAGY"
                      value="1"
                      onChange={handleInputChange}
                      checked={formData.HBSAG === '1'}
                      required
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioISHBSAGY">
                      متفاعل
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="HBSAG"
                      id="RadioISHBSAGN"
                      value="0"
                      onChange={handleInputChange}
                      checked={formData.HBSAG === '0'}
                      required
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioISHBSAGN">
                      غير متفاعل
                    </label>
                  </div>
                </div>
                <span className="field-validation-valid text-danger" data-valmsg-for="ISHBSAG" data-valmsg-replace="true"></span>
              </div>
              <div className="col-md-3 form-group" id="ISANTIHIVDIV">
                <input id="ISANTIHIV" name="ISANTIHIV" type="hidden" value={formData.ANTIHIV} />

                <label>
                  Anti-HIV <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ANTIHIV"
                      id="RadioISANTIHIVY"
                      value="1"
                      onChange={handleInputChange}
                      checked={formData.ANTIHIV === '1'}
                      required
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioISANTIHIVY">
                      متفاعل
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ANTIHIV"
                      id="RadioISANTIHIVN"
                      value="0"
                      onChange={handleInputChange}
                      checked={formData.ANTIHIV === '0'}
                      required
                      title="برجاء الاختيار"
                    />
                    <label className="form-check-label" htmlFor="RadioISANTIHIVN">
                      غير متفاعل
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="row" style={{fontSize: '16px'}}>
              <div className="col-md-3 form-group" id="BLOODTYPEDIV">
                <label>
                  فصيلة الدم <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <label style={{width: '21em'}}>
                    <select
                      className="custom-select d-block w-100 col-md-12"
                      id="BLOODTYPEIDFK"
                      name="BLOODTYPEIDFK"
                      value={formData.BLOODTYPEIDFK}
                      onChange={handleInputChange}
                      required
                      title="برجاء الاختيار"
                    >
                      <option value="">اختر</option>
                      {referenceData.bloodTypes.map(bloodType => (
                        <option key={bloodType.id} value={bloodType.id}>
                          {bloodType.name}
                        </option>
                      ))}
                    </select>
                    <span className="field-validation-valid text-danger" data-valmsg-for="BLOODTYPEIDFK" data-valmsg-replace="true"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <input id="PartnerBirthDate" name="PartnerBirthDate" type="hidden" value="" readOnly />
          <input id="PartnerAge" name="PartnerAge" type="hidden" value="" readOnly />
        </form>

        <div className="table-responsive" hidden id="tableDIV">
          <table className="table table-hover table-striped table-bordered" id="SheetsData">
            <thead>
              <tr>
                <th>تاريخ التسجيل</th>
                <th>الرقم القومى</th>
                <th>رقم الاستمارة</th>
                <th>الاسم</th>
                <th>مكان استخراج الاستمارة</th>
              </tr>
            </thead>
            <tbody id="SheetsDataBody"></tbody>
          </table>
        </div>
      </div>

      <center>
        <button
          type="submit"
          form="createForm"
          className="btn btn-success btn-md loading"
          id="SaveBtn"
          disabled={isSubmitting}
        >
          <i className="fas fa-save"></i> {isSubmitting ? 'جاري الحفظ...' : 'حفظ'}
        </button> <button
          type="button"
          onClick={() => window.history.back()}
          className="btn btn-danger btn-md"
          style={{color: 'white'}}
        >
          <i className="fas fa-window-close"></i> إلغاء
        </button>
      </center>

        </div>
      </main>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal" role="dialog" id="submitMsg" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-confirm" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">تسجيل الاستمارة</h4>
              </div>
              <div className="modal-body">
                <p style={{textAlign: 'center', fontSize: '19px'}}>تم تسجيل الاستمارة بنجاح</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-danger" onClick={handleCloseSuccessModal}>
                  إغلاق <i className="fas fa-window-close"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CreatePrenupForm;
